import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../../store";
import {
  Button,
  Table,
  TableTR,
  Filter,
  FilterValue,
  toast,
} from "../../../components";
import { Avatar } from "@/utils/ui/Avatar/Avatar";
import { formatEntityName } from "@/utils/utilFunctions/entityName";
import AddAgencyForm from "@/components/Agency/AddAgencyForm";
import {
  getListAgencies,
  getListBunchAgencies,
  archiveAgency,
} from "@/store/constants/constants.action";
import { setAgenciesList } from "@/store/constants/constants.slice";
import { useAppDispatch } from "../../../store";

import { Option } from "@/database";

import Modal from "@/components/Modal/Modal";

import PageHeader from "@/components/Layout/PageHeader";
import { Edit } from "lucide-react";

import { NoContentFound } from "@/utils/ui/NoContent";

const headers: { name: string; width: string }[] = [
  { name: "Name", width: "35%" },
  { name: "Agency Group", width: "15%" },
  { name: "Created On", width: "15%" },
  { name: "Contract Expiry Date", width: "15%" },
  { name: "Status", width: "10%" },
  { name: "Action", width: "10%" },
];

const AgencyList = ({
  getListAgencies,
  setAgenciesList,
  agenciesList,
  isClearFilter,
  isLoading,
  bunchAgenciesList,
  getListBunchAgencies,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedAgency, setSelectedAgency] = useState<any>(null);
  const [formState, setFormState] = useState<{
    isDirty: boolean;
    isValid: boolean;
  }>({
    isDirty: false,
    isValid: false,
  });
  const [filters, setFilters] = useState<Record<string, FilterValue>>({});
  const [availableFilters, setAvailableFilters] = useState<Filter[]>([]);

  const [autoselectAgency, setAutoselectAgency] = useState<any[]>([]);
  const [archivingId, setArchivingId] = useState<number | null>(null);
  const dispatch = useAppDispatch();

  const handleSetFilters = (filters: Record<string, FilterValue>) => {
    setFilters(filters);
  };

  const handleModalOpen = () => setIsModalOpen(true);
  const handleModalClose = () => {
    setSelectedAgency(null);
    setIsModalOpen(false);
  };

  useEffect(() => {
    setAgenciesList({
      data: { list: [], count: 0, isLoading: true, error: "" },
    });
  }, [setAgenciesList]);

  useEffect(() => {
    if (availableFilters.length > 0) return; // Prevent unnecessary updates

    const agenciesConstOptionArray: Option[] = autoselectAgency?.map(
      (value: any) => ({
        label: formatEntityName(value),
        value: value.id,
        displayValue: formatEntityName(value),
      })
    );

    setAvailableFilters([
      {
        displayName: "Name",
        name: "id",
        type: "autoselect",
        suffix: "contains",
        options: agenciesConstOptionArray,
      },
      {
        displayName: "Keyword",
        name: "name",
        type: "text",
        suffix: "Contains",
      },
      {
        displayName: "Status",
        name: "status",
        type: "radio",
        suffix: ":",
        options: [
          { label: "Active", value: "1", displayValue: "Active" },
          { label: "Inactive", value: "2", displayValue: "Inactive" },
        ],
      },
    ]);
    // setIsLoading(false);
  }, [autoselectAgency]); // Updated dependency array

  const filteredList = agenciesList.list
    .filter((agency: any) => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        switch (key) {
          case "status":
            return agency.status;
          default:
            return true;
        }
      });
    })
    .sort((a: any, b: any) => {
      const keyword =
        typeof filters?.name === "string"
          ? filters.name.trim().toLowerCase()
          : "";
      if (!keyword) return 0;
      const aName = (a.name || "").toLowerCase();
      const bName = (b.name || "").toLowerCase();
      const score = (n: string) =>
        n === keyword
          ? 0
          : n.startsWith(keyword)
            ? 1
            : n.includes(keyword)
              ? 2
              : 3;
      return score(aName) - score(bName);
    });

  const handleArchiveToggle = (item: any) => {
    if (archivingId === item.id) return;
    setArchivingId(item.id);
    const newArchive = item.archive === 1 ? 0 : 1;
    dispatch(
      archiveAgency(
        { id: item.id, archive: newArchive },
        () => {
          toast.success(
            newArchive === 1
              ? "Agency marked as Inactive"
              : "Agency marked as Active"
          );
          setArchivingId(null);
        },
        () => setArchivingId(null)
      )
    );
  };

  const handleEdit = (agency: any) => {
    setSelectedAgency(agency);
    setIsModalOpen(true);
  };

  const handleSaveForm = (e: any) => {
    e.preventDefault();
    const event = new Event("submit", {
      bubbles: true,
      cancelable: true,
    });
    window.dispatchEvent(event);
  };

  return (
    <div className="container">
      <PageHeader
        title="AGENCY"
        buttonText="ADD NEW"
        onButtonClick={handleModalOpen}
        backPath="/setting"
      />
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          title={
            selectedAgency
              ? `Edit Agency: ${formatEntityName(selectedAgency)}`
              : "Add New Agency"
          }
          width="90vw"
          closeOnOutsideClick={false}
          footer={
            <div className="d-flex justify-content-end w-100">
              <Button type="light" onClick={handleModalClose} className="me-2">
                Cancel
              </Button>
              <Button
                type="primary"
                isDisabled={
                  !formState.isDirty
                  // || !formState.isValid
                }
                onClick={(e) => handleSaveForm(e)}
              >
                Save
              </Button>
            </div>
          }
        >
          <AddAgencyForm
            onClose={handleModalClose}
            onFormStateChange={setFormState}
            agency={selectedAgency}
          />
        </Modal>
      )}
      <Table
        columns={headers}
        isLoading={isLoading}
        totalCount={agenciesList.count}
        // onFilterChange={handleFilterChange}
        handleSetFilters={handleSetFilters}
        appliedFilters={filters}
        availableFilters={availableFilters}
        isClearFilter={isClearFilter}
        filters={filters}
        apiCallDelayTimer={0}
        isSetinParams={false}
        getListMedia={getListAgencies}
        stopIfNoFilter={false}
        autoSelectLoader={true}
        onAutoSelectInputChange={(params) => {
          const keyword = (params.keyword || "").trim();
          if (keyword.length >= 3) {
            if (params.name === "id") {
              getListBunchAgencies({ name: keyword }).then((res: any) => {
                setAutoselectAgency(res);
              });
            }
          } else {
            if (params.name === "id") {
              setAutoselectAgency([]);
            }
          }
        }}
        autoSelectOptionsMap={{
          id: (() => {
            const agencyOptions = (autoselectAgency || [])?.map(
              (item: any) => ({
                label: formatEntityName(item),
                value: item?.id,
              })
            );

            return agencyOptions;
          })(),
        }}
      >
        {agenciesList.list.length > 0 ? (
          filteredList.map((item: any) => (
            <TableTR key={item.id} rowKey={item.id}>
              <td valign="middle">
                <div className="d-flex align-items-center gap-2">
                  <Avatar text={item.name} size={30} fontsize={15} />
                  <span>{formatEntityName(item)}</span>
                </div>
              </td>
              <td valign="middle">
                <div className="d-flex align-items-center">
                  {item.agency_group}
                </div>
              </td>
              <td valign="middle">
                <div className="d-flex align-items-center">
                  {item?.ct ? new Date(item?.ct).toLocaleDateString() : "--"}
                </div>
              </td>
              <td valign="middle">
                <div className="d-flex align-items-center">
                  {item?.settings?.expiry
                    ? new Date(item?.settings?.expiry).toLocaleDateString()
                    : "--"}
                </div>
              </td>
              <td valign="middle">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    opacity: archivingId === item.id ? 0.5 : 1,
                    cursor: archivingId === item.id ? "wait" : "pointer",
                  }}
                  onClick={() => handleArchiveToggle(item)}
                >
                  <div
                    style={{
                      width: 36,
                      height: 20,
                      borderRadius: 10,
                      background: item.archive === 1 ? "#ccc" : "#2563eb",
                      position: "relative",
                      transition: "background 0.2s",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 3,
                        left: item.archive === 1 ? 3 : 19,
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: "#fff",
                        transition: "left 0.2s",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 13,
                      color: item.archive === 1 ? "#888" : "#2563eb",
                      fontWeight: 500,
                    }}
                  >
                    {item.archive === 1 ? "Inactive" : "Active"}
                  </span>
                </div>
              </td>
              <td valign="middle">
                <div className="d-flex align-items-center">
                  <span
                    className="px-2 btn btn-outline-secondary"
                    onClick={() => handleEdit(item)}
                  >
                    <Edit size={16} />
                  </span>
                </div>
              </td>
            </TableTR>
          ))
        ) : (
          <>
            {!isLoading && filteredList.length === 0 && (
              <TableTR rowKey="no-content">
                <td colSpan={headers.length}>
                  <NoContentFound message="No Agencies Found" fill />
                </td>
              </TableTR>
            )}
          </>
        )}
      </Table>
    </div>
  );
};

const mapState = (state: RootState) => ({
  isClearFilter: state.media.isClearFilter,
  agenciesList: state.constants.agenciesList,
  isLoading: state.constants.agenciesList.isLoading,
  bunchAgenciesList: state.constants.bunchAgenciesList.list,
});

const mapDispatch = {
  getListAgencies,
  setAgenciesList,
  getListBunchAgencies,
  archiveAgency,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;
export default connector(AgencyList);
